<template>
  <div class="date-picker" :class="{ [variant]: !!variant }">
    <div class="__period-container" v-if="variant === 'full' && range">
      <ul>
        <li
          v-for="date in datePickerValues"
          :key="date.period + date.unit"
          :class="{ selected: isSelected(date.period, date.unit) }"
          @click="onPresetClicked(date.period, date.unit)"
        >
          {{ $t(`date-picker.${date.period}.${date.unit}`) }}
        </li>
      </ul>
    </div>
    <VDatePicker
      v-model="dates"
      no-title
      :range="range"
      :locale="userLocale"
      :first-day-of-week="1"
      :picker-date.sync="pickerDate"
    >
      <div class="date-picker__append">
        <div class="date-picker__notice">{{ notice }}</div>
        <v-list-item class="date-picker__append--separator"> </v-list-item>
        <v-list-item class="date-picker__append--buttons">
          <v-list-item-content>
            <v-list-item-title>
              <span @click="clearDates" class="date-picker__append--button">{{
                $t('dialog.filters.advanced.clear')
              }}</span>
            </v-list-item-title>
            <v-list-item-title>
              <span @click="saveDates" class="date-picker__append--apply-button" :class="{ disabled: isApplyDisabled }">
                {{ $t('dialog.filters.advanced.apply') }}</span
              >
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </div>
    </VDatePicker>
  </div>
</template>

<script>
import moment from 'moment';
import { VDatePicker } from 'vuetify/lib';
import {
  getStartOfPeriod,
  getEndOfPeriod,
  getCurrentPeriod,
  calculatePreviousPeriod,
  formatDateToString,
  getToday,
} from '@/util/date-util';
import { DATE_PICKER_PERIOD } from '@/util/constants/date-picker-periods';

export default {
  name: 'DatePicker',
  components: {
    VDatePicker,
  },
  props: {
    variant: {
      type: String,
      default: 'standard', // <standard, full>
    },
    range: {
      type: Boolean,
      required: false,
    },
    propDates: {
      type: Array,
      required: false,
    },
    propDate: {
      type: String,
      required: false,
    },
    notice: {
      type: String,
      required: false,
      default: null,
    },
    rangeRequired: {
      type: Boolean,
      default: false,
      required: false,
    },
    datePickerValues: {
      type: Array,
      required: false,
      default: () => DATE_PICKER_PERIOD,
    },
  },
  data() {
    return {
      dates: this.propDates ? this.propDates : this.propDate,
      isDirty: false,
      pickerDate: null,
    };
  },
  computed: {
    userLocale() {
      return this.$i18n.locale;
    },
    hasNoChanges() {
      const propDates = this.propDates ? this.propDates : this.propDate;
      return propDates === this.dates || (propDates.length < 1 && this.dates.length < 1);
    },
    isApplyDisabled() {
      return this.hasNoChanges || (this.rangeRequired && this.dates.length !== 2);
    },
  },
  methods: {
    clearDates() {
      if (this.dates.length === 0) {
        this.$emit('on-close-menu');
        return;
      }

      if (typeof this.dates === 'string') {
        this.dates = '';
      } else {
        this.dates = [];
      }
      this.isDirty = false;
      this.$emit('on-clear-dates');
    },
    saveDates() {
      if (this.isApplyDisabled) {
        return;
      }
      this.isDirty = false;
      this.dates = this.dates.sort((a, b) => a.localeCompare(b));
      this.$emit('on-save-dates', this.dates);
    },
    onPresetClicked(period, unit) {
      // only works if dates are a range
      if (this.range) {
        this.$nextTick(() => {
          if (period === 'current') {
            if (unit === 'ytd') {
              this.dates = [getStartOfPeriod(moment(), 'year'), getToday()];
            } else {
              this.dates = [getStartOfPeriod(moment(), unit), getEndOfPeriod(moment(), unit)];
            }
          } else {
            if (unit === '12months') {
              this.dates = [formatDateToString(moment().subtract(12, 'month'), 'month'), getToday()];
            } else {
              this.dates = [
                getStartOfPeriod(moment().subtract(1, unit), unit),
                getEndOfPeriod(moment().subtract(1, unit), unit),
              ];
            }
          }
          this.pickerDate = this.range ? this.dates[1] : this.dates;
        });
      }
    },
    isSelected(period, unit) {
      // only works if dates are a range
      if (this.range) {
        if (period === 'current') {
          if (unit === 'ytd') {
            const currentPeriod = [getStartOfPeriod(moment(), 'year'), getToday()];
            return this.dates[0] === currentPeriod[0] && this.dates[1] === currentPeriod[1];
          } else {
            const currentPeriod = getCurrentPeriod(unit);
            return this.dates[0] === currentPeriod[0] && this.dates[1] === currentPeriod[1];
          }
        } else {
          if (unit === '12months') {
            const previousPeriod = [formatDateToString(moment().subtract(12, 'month'), 'month'), getToday()];
            return this.dates[0] === previousPeriod[0] && this.dates[1] === previousPeriod[1];
          } else {
            const startOfPeriod = moment(getStartOfPeriod(moment(), unit));
            const previousPeriod = calculatePreviousPeriod(startOfPeriod, unit);
            return this.dates[0] === previousPeriod[0] && this.dates[1] === previousPeriod[1];
          }
        }
      }
      return false;
    },
  },
  watch: {
    propDates: {
      deep: true,
      handler() {
        if (this.propDates) {
          // could potentially be a single date or a range
          if (this.propDates.length === 1) {
            this.dates = [formatDateToString(this.propDates[0])];
          } else {
            this.dates = [formatDateToString(this.propDates[0]), formatDateToString(this.propDates[1])];
          }
        } else {
          this.dates = this.propDate;
        }
      },
    },
  },
};
</script>

<style lang="scss">
.date-picker {
  display: flex;
  flex-direction: row;
  background: $color-white;

  &__menu {
    border-radius: $border-radius-root;
  }

  &.full {
    .v-card {
      border-radius: 0px !important;
      box-shadow: none;
      border-left: 1px solid $color-border;
    }

    .__period-container {
      min-width: 170px;
      align-self: start;
      padding: 12px 0;

      ul {
        list-style-type: none;

        li {
          cursor: pointer;
          padding: 6px 12px;
          font-size: $font-size-small;

          &.selected {
            color: $color-accent;
            background-color: $color-accent-100;
            border-left: 3px solid $color-accent;
          }

          &:hover {
            background-color: $color-neutral-pale;
          }
        }
      }
    }

    // Phones: the periods on a line of pills that scrolls sideways, above the calendar across the
    // screen; side by side they were wider than it.
    @include screen-below('tablet') {
      flex-direction: column;

      .__period-container {
        min-width: 0;
        align-self: stretch;
        padding: 0 0 6px;

        ul {
          display: flex;
          gap: 8px;
          margin: 0;
          // Room for the pills' 44px touch areas, which the sideways scroll would clip.
          padding: 6px 0;
          overflow-x: auto;
          scrollbar-width: none;

          &::-webkit-scrollbar {
            display: none;
          }

          li {
            flex: none;
            display: flex;
            align-items: center;
            min-height: 32px;
            padding: 0 12px;
            border: 1px solid $color-border;
            border-radius: $radius-full;
            white-space: nowrap;
            @include phone-touch-area;

            &.selected {
              border: 1px solid $color-accent;
            }
          }
        }
      }

      .v-card {
        border-left: 0;
      }

      .v-picker,
      .v-picker__body {
        width: 100% !important;
      }
    }
  }

  &__notice {
    color: $color-normal-pale;
    font-style: italic;
    padding: 0 16px;
  }
}

// In a sheet (BaseBottomSheet), flat and across it: the sheet is its card.
.base-bottom-sheet .date-picker {
  .v-card {
    border-radius: 0 !important;
    box-shadow: none;
  }

  .v-picker,
  .v-picker__body {
    width: 100% !important;
  }
}

// The date picker renders its own Clear/Apply actions with these class names. Styling lives here
// so the picker stands alone wherever it's used.
.date-picker__append {
  color: $color-accent;
  font-size: $font-size-small;
  min-height: 50px;
  background: $color-white !important;
  border-radius: $border-radius-root;
  position: sticky;
  bottom: 0;
  align-items: center;
  width: 100%;

  &--separator {
    border-bottom: $grey-border;
    height: 1px;
    min-height: 1px !important;
    margin-top: 16px !important;
    padding: 0;
  }

  &--buttons {
    padding: 0 8px !important;
    display: flex;
    justify-content: space-between;
  }

  .v-list-item {
    padding: 0px;

    &__content {
      padding-top: 12px !important;
      margin-left: 0px !important;
      justify-content: space-between;
      gap: 8px;
    }

    &__title {
      // Vuetify defaults list titles to flex:1 1 100%, which would wrap the two actions onto
      // separate lines — size them to content so Clear/Apply sit side by side.
      flex: 0 1 auto;
      background-color: $color-white;
      padding: 4px;
    }
  }

  &--button {
    border-radius: $border-radius-small !important;
    cursor: pointer;
    padding: 6px;
    text-decoration: underline;
    font-weight: bold !important;
    color: $color-normal !important;
    text-transform: capitalize;
    letter-spacing: 0ch;
    font-size: 14px;
    transition: all 200ms ease;

    &:hover {
      transition: all 200ms ease;
      background-color: $color-hover !important;
    }
  }

  &--apply-button {
    border-radius: $border-radius-small !important;
    cursor: pointer;
    padding: 6px;
    font-weight: bold !important;
    color: $color-white !important;
    background-color: $color-accent;
    text-transform: capitalize;
    letter-spacing: 0ch;
    font-size: 14px;
    transition: all 200ms ease;

    &.disabled {
      box-shadow: none;
      cursor: not-allowed;
      opacity: 0.6;
    }

    &:hover {
      transition: all 200ms ease;
      background-color: $color-normal !important;
    }
  }
}
</style>
